import React, { useState, useCallback } from 'react';
import { XCircle, Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { newSelect as Select } from '../ui/newSelect';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const reasonOptions = [
  { value: 'de_minimis', label: 'De minimis (below threshold)' },
  { value: 'no_data_available', label: 'No data available' },
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'operational_control', label: 'Outside operational control' },
  { value: 'other', label: 'Other' },
];

const scopeOptions = [
  { value: '1', label: 'Scope 1' },
  { value: '2', label: 'Scope 2' },
  { value: '3', label: 'Scope 3' },
];

const emptyExclusion = {
  category: '',
  scope: '3',
  ghgCategory: '',
  reason: 'de_minimis',
  justification: '',
  estimatedEmissions: '',
  materialityPct: '',
  isoReference: 'ISO 14064-1:2018 clause 6.4',
};

/**
 * Exclusion register (Screen 20).
 * @param {object} props
 * @param {Array} props.exclusions
 * @param {Function} [props.onAdd]
 * @param {Function} [props.onUpdate]
 * @param {Function} [props.onDelete]
 */
const ExclusionRegister = ({ exclusions = [], onAdd, onUpdate, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyExclusion);

  const totalExcluded = exclusions.reduce((sum, e) => sum + (e.estimatedEmissions || 0), 0);

  const handleEdit = useCallback((excl) => {
    setEditItem(excl);
    setForm({ ...excl, scope: String(excl.scope) });
    setShowModal(true);
  }, []);

  const handleAdd = useCallback(() => {
    setEditItem(null);
    setForm(emptyExclusion);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(() => {
    const data = {
      ...form,
      scope: parseInt(form.scope) || 3,
      estimatedEmissions: parseFloat(form.estimatedEmissions) || 0,
      materialityPct: parseFloat(form.materialityPct) || 0,
    };
    if (editItem) {
      onUpdate?.({ ...editItem, ...data });
    } else {
      onAdd?.(data);
    }
    setShowModal(false);
  }, [form, editItem, onAdd, onUpdate]);

  return (
    <TooltipProvider>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <h2 className='text-lg font-semibold flex items-center gap-2'>
            <XCircle size={20} strokeWidth={1.5} />
            Exclusion Register
          </h2>
          <div className='flex items-center gap-3'>
            {totalExcluded > 0 && (
              <span className='text-sm text-muted-foreground'>
                Total excluded: {totalExcluded.toFixed(1)} tCO₂e
              </span>
            )}
            <Button size='sm' onClick={handleAdd}>
              <Plus size={14} strokeWidth={1.5} />
              Add Exclusion
            </Button>
          </div>
        </div>

        <Card className='bg-card'>
          <CardContent className='p-0'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className='text-right'>Est. Emissions</TableHead>
                  <TableHead className='text-right'>Materiality</TableHead>
                  <TableHead>ISO Reference</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exclusions.map((excl) => (
                  <TableRow key={excl.id}>
                    <TableCell className='font-medium text-sm'>{excl.category}</TableCell>
                    <TableCell className='text-sm'>Scope {excl.scope}</TableCell>
                    <TableCell>
                      <Badge className='bg-muted text-muted-foreground text-xs'>
                        {excl.reason?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-right font-mono text-sm'>
                      {(excl.estimatedEmissions || 0).toFixed(1)} tCO₂e
                    </TableCell>
                    <TableCell className='text-right text-sm'>
                      {excl.materialityPct != null ? `${excl.materialityPct}%` : '—'}
                    </TableCell>
                    <TableCell className='text-xs text-muted-foreground'>{excl.isoReference || '—'}</TableCell>
                    <TableCell>
                      <div className='flex gap-1'>
                        <Tooltip>
                          <TooltipTrigger>
                            <Button variant='ghost' size='sm' onClick={() => handleEdit(excl)}>
                              <Pencil size={14} strokeWidth={1.5} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger>
                            <Button variant='ghost' size='sm' className='text-danger' onClick={() => onDelete?.(excl.id)}>
                              <Trash2 size={14} strokeWidth={1.5} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {exclusions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className='text-center py-8 text-muted-foreground'>
                      No exclusions documented. Add exclusions for any emission categories you are intentionally omitting.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className='max-w-lg'>
            <DialogHeader>
              <DialogTitle>{editItem ? 'Edit' : 'Add'} Exclusion</DialogTitle>
            </DialogHeader>
            <div className='flex flex-col gap-3'>
              <div>
                <label className='text-sm font-medium mb-1 block'>Category *</label>
                <input
                  type='text'
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder='e.g. Employee Commuting'
                  className='w-full rounded-md border border-border bg-input px-3 py-2 text-sm'
                />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='text-sm font-medium mb-1 block'>Scope</label>
                  <Select
                    value={form.scope}
                    onValueChange={(v) => setForm({ ...form, scope: v })}
                    options={scopeOptions}
                  />
                </div>
                <div>
                  <label className='text-sm font-medium mb-1 block'>GHG Category</label>
                  <input
                    type='text'
                    value={form.ghgCategory}
                    onChange={(e) => setForm({ ...form, ghgCategory: e.target.value })}
                    placeholder='e.g. Category 7'
                    className='w-full rounded-md border border-border bg-input px-3 py-2 text-sm'
                  />
                </div>
              </div>
              <div>
                <label className='text-sm font-medium mb-1 block'>Reason</label>
                <Select
                  value={form.reason}
                  onValueChange={(v) => setForm({ ...form, reason: v })}
                  options={reasonOptions}
                />
              </div>
              <div>
                <label className='text-sm font-medium mb-1 block'>Justification *</label>
                <textarea
                  value={form.justification}
                  onChange={(e) => setForm({ ...form, justification: e.target.value })}
                  placeholder='Explain why this category is excluded'
                  rows={3}
                  className='w-full rounded-md border border-border bg-input px-3 py-2 text-sm resize-none'
                />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='text-sm font-medium mb-1 block'>Est. Emissions (tCO₂e)</label>
                  <input
                    type='number'
                    step='0.1'
                    value={form.estimatedEmissions}
                    onChange={(e) => setForm({ ...form, estimatedEmissions: e.target.value })}
                    className='w-full rounded-md border border-border bg-input px-3 py-2 text-sm'
                  />
                </div>
                <div>
                  <label className='text-sm font-medium mb-1 block'>Materiality (%)</label>
                  <input
                    type='number'
                    step='0.1'
                    value={form.materialityPct}
                    onChange={(e) => setForm({ ...form, materialityPct: e.target.value })}
                    className='w-full rounded-md border border-border bg-input px-3 py-2 text-sm'
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={!form.category || !form.justification}>
                {editItem ? 'Update' : 'Add'} Exclusion
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default ExclusionRegister;
